import type {
  ConfigSnapshotPayload,
  EmbedKind,
  GroupPayload,
  WidgetPayload,
} from "@workspace/contracts/embed"

/**
 * A hand-written shape check on what `GET /api/embed/{kind}/{id}` answered
 * - enough to fail safe on a malformed response, no more. The server
 * already parsed the payload through the CLOSED `configSnapshotPayloadSchema`
 * before serving it (`EmbedService.config`), so this trusts the fields
 * inside and only asks: is this the envelope, with the kind I asked for,
 * carrying an object with an id and a config? Shipping zod (~12KB) to
 * every merchant page for a check the publish already made is the thing
 * this avoids.
 */

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null

function isWidget(value: unknown): value is WidgetPayload {
  return (
    isObject(value) &&
    typeof value.id === "string" &&
    isObject(value.config) &&
    typeof value.config.type === "string" &&
    isObject(value.config.appearance) &&
    Array.isArray(value.rules)
  )
}

function isGroup(value: unknown): value is GroupPayload {
  return (
    isObject(value) &&
    typeof value.id === "string" &&
    typeof value.mode === "string" &&
    isObject(value.config) &&
    Array.isArray(value.members) &&
    value.members.every((m) => isObject(m) && isWidget(m.widget)) &&
    Array.isArray(value.rules)
  )
}

export function guardSnapshot(
  value: unknown,
  kind: "widget"
): Extract<ConfigSnapshotPayload, { kind: "widget" }> | null
export function guardSnapshot(
  value: unknown,
  kind: "group"
): Extract<ConfigSnapshotPayload, { kind: "group" }> | null
export function guardSnapshot(
  value: unknown,
  kind: EmbedKind
): ConfigSnapshotPayload | null
export function guardSnapshot(
  value: unknown,
  kind: EmbedKind
): ConfigSnapshotPayload | null {
  if (!isObject(value) || value.kind !== kind) return null
  if (kind === "widget" && isWidget(value.payload)) {
    return value as unknown as ConfigSnapshotPayload
  }
  if (kind === "group" && isGroup(value.payload)) {
    return value as unknown as ConfigSnapshotPayload
  }
  return null
}
